import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  Paper,
  Autocomplete,
  Box,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import {
  getCodingDiagnosisAutosuggest,
  addApolloDiseaseDetection,
  addCodingApolloDiseaseDetection,
} from '../../api';

const TaskModal = ({ username, token, open, onClose, action }) => {
  const [apolloComment, setApolloComment] = useState('');
  const [diagnosisHCC, setDiagnosisHCC] = useState('');
  const [isApolloAdded, setIsApolloAdded] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [markAsResolved, setMarkAsResolved] = useState(false);

  useEffect(() => {
    if (action) {
      const hccString = `${action.diagnosis_code}/${action.hcc_code}`;
      setDiagnosisHCC(hccString);
    }
  }, [action]);

  const fetchSuggestions = async (input) => {
    try {
      const response = await getCodingDiagnosisAutosuggest({ username, token }, input);
      setSuggestions(response || []);
    } catch (error) {
      console.error('Error fetching suggestions:', error);
    }
  };

  const handleApolloCommentChange = (e) => setApolloComment(e.target.value);

  const handleSendToApollo = () => {
    if (!action) return;
    const timestamp = Date.now().toString();
    const patientId = parseInt(action.patient_id);
    
    const finalComment = markAsResolved ? `Resolved - ${apolloComment}` : apolloComment;

    addApolloDiseaseDetection(patientId, { username, token }, diagnosisHCC, finalComment, 'Clinical_Leadership', timestamp);
    addCodingApolloDiseaseDetection(patientId, { username, token }, diagnosisHCC, finalComment, 'Clinical_Leadership', timestamp);
    setIsApolloAdded(true);
  };

  const handleResetAndClose = () => {
    setApolloComment('');
    setDiagnosisHCC('');
    setSuggestions([]);
    setIsApolloAdded(false);
    setMarkAsResolved(false);
    onClose();
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog
        open={open}
        onClose={handleResetAndClose}
        maxWidth={false}
        PaperProps={{ sx: { width: '600px' } }}
      >
        <DialogTitle>Send Apollo Feedback</DialogTitle>
        <DialogContent>
          {action ? (
            <>
              <Typography variant="body1"><strong>Patient ID:</strong> {parseInt(action.patient_id)}</Typography>
              <Typography variant="body1"><strong>Diagnosis:</strong> {action.diagnosis_code}</Typography>
              <Typography variant="body1" sx={{ mb: 2 }}><strong>HCC Code:</strong> {action.hcc_code}</Typography>

              <Autocomplete
                freeSolo
                value={diagnosisHCC}
                options={suggestions}
                onInputChange={(e, value) => {
                  setDiagnosisHCC(value);
                  fetchSuggestions(value);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label="Diagnosis/HCC"
                    fullWidth
                    sx={{ mt: 2 }}
                  />
                )}
              />
              <TextField
                fullWidth
                multiline
                rows={4}
                variant="outlined"
                label="Enter Apollo feedback"
                value={apolloComment}
                onChange={handleApolloCommentChange}
                sx={{ mt: 2 }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={markAsResolved}
                    onChange={(e) => setMarkAsResolved(e.target.checked)}
                    name="markAsResolved"
                  />
                }
                label="Mark as resolved"
                sx={{ mt: 1, mb: 1 }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleSendToApollo}
                  disabled={isApolloAdded}
                  sx={{ mt: 1 }}
                >
                  {isApolloAdded ? 'Feedback Sent' : 'Send to Apollo'}
                </Button>
              </Box>
            </>
          ) : (
            <Typography>No action selected</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleResetAndClose} color="primary">Close</Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default TaskModal;
